import {
  Combobox,
  ComboboxOption,
  FieldWrap,
  Loader,
  Pagination
} from '@deque/cauldron-react';
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import SecurityEventLogDetail from '~/app/components/SecurityEventLog/SecurityEventLogDetail';
import SecurityEventLogFilters, {
  type FilterState
} from '~/app/components/SecurityEventLog/SecurityEventLogFilters';
import SecurityEventLogTable from '~/app/components/SecurityEventLog/SecurityEventLogTable';
import { useSecurityEventLog } from '~/app/hooks/useSecurityEventLog';
import { useAuthContext } from '~/common/contexts/auth';
import { useAllEnterprises } from '~/common/hooks/useAllEnterprises';
import type { SecurityEvent } from '~/common/utils/admin-client/get-security-event-log';
import {
  exportSecurityEventLog,
  SECURITY_EVENT_LOG_CSV_FILENAME
} from '~/common/utils/admin-client/get-security-event-log';
import { downloadBlob } from '~/common/utils/downloadBlob';
import ContentToast from '~/modules/common/components/ContentToast';
import styles from './SecurityEventLogTab.css';

const ITEMS_PER_PAGE = 50;

const defaultFilters = (): FilterState => ({
  startDate: '',
  endDate: '',
  eventType: '',
  category: '',
  actorId: '',
  adminOnly: false
});

const SecurityEventLogTab: React.FC = () => {
  const { t } = useTranslation();
  const { user } = useAuthContext();
  const token = user?.token ?? '';

  const {
    data: enterprises,
    loading: loadingEnterprises,
    error: enterprisesError
  } = useAllEnterprises({ token });

  const [selectedEnterprise, setSelectedEnterprise] = useState<string | null>(
    null
  );
  const [pending, setPending] = useState<FilterState>(defaultFilters);
  const [applied, setApplied] = useState<FilterState>(defaultFilters);
  const [currentPage, setCurrentPage] = useState(1);
  const [selectedEvent, setSelectedEvent] = useState<SecurityEvent | null>(
    null
  );
  const [exportError, setExportError] = useState<Error | null>(null);
  const [errorDismissed, setErrorDismissed] = useState(false);

  const { events, totalItems, loading, error, refresh } = useSecurityEventLog({
    token,
    enterpriseId: selectedEnterprise,
    start_date: applied.startDate || undefined,
    end_date: applied.endDate || undefined,
    event_type: applied.eventType || undefined,
    category: applied.category || undefined,
    user_id: applied.actorId || undefined,
    admin_only: applied.adminOnly || undefined,
    page: currentPage,
    per_page: ITEMS_PER_PAGE
  });

  // Reset the dismissed flag whenever a new error arrives so a fresh toast
  // is shown — the hook returns a new Error instance on each failed fetch.
  useEffect(() => {
    if (error) {
      setErrorDismissed(false);
    }
  }, [error]);

  const handleFilterChange = <K extends keyof FilterState>(
    field: K,
    value: FilterState[K]
  ) => {
    setPending(prev => ({ ...prev, [field]: value }));
  };

  const handleApply = () => {
    setApplied({ ...pending });
    setCurrentPage(1);
  };

  const handleClear = () => {
    const cleared = defaultFilters();
    setPending(cleared);
    setApplied(cleared);
    setCurrentPage(1);
  };

  const handleExport = async () => {
    setExportError(null);
    try {
      const blob = await exportSecurityEventLog({
        token,
        enterpriseId: selectedEnterprise,
        start_date: applied.startDate || undefined,
        end_date: applied.endDate || undefined,
        event_type: applied.eventType || undefined,
        category: applied.category || undefined,
        user_id: applied.actorId || undefined,
        admin_only: applied.adminOnly || undefined
      });
      downloadBlob(blob, SECURITY_EVENT_LOG_CSV_FILENAME);
    } catch (err) {
      setExportError(err instanceof Error ? err : new Error(String(err)));
    }
  };

  // Selection is driven by per-option `onClick` handlers below so we can key
  // off `enterprise.id` directly — keying off `enterprise.name` would pick
  // the wrong enterprise if two share a name, and would also conflict with
  // an enterprise literally named "All".
  const selectAllEnterprises = () => {
    setSelectedEnterprise(null);
    setCurrentPage(1);
  };

  const selectEnterprise = (enterpriseId: string) => {
    setSelectedEnterprise(enterpriseId);
    setCurrentPage(1);
  };

  const totalPages = Math.max(1, Math.ceil(totalItems / ITEMS_PER_PAGE));
  const rangeStart =
    totalItems === 0 ? 0 : (currentPage - 1) * ITEMS_PER_PAGE + 1;
  const rangeEnd = Math.min(currentPage * ITEMS_PER_PAGE, totalItems);

  return (
    <>
      {enterprisesError && (
        <ContentToast type="caution" show>
          <strong>{t('Error loading enterprises -')}</strong>
          <code>{enterprisesError.message}</code>
        </ContentToast>
      )}

      {loadingEnterprises ? (
        <Loader label={t('Loading enterprises...')} tabIndex={-1} />
      ) : (
        <FieldWrap className={styles.fieldWrap}>
          <Combobox
            label={t('Search for an enterprise')}
            name="security-enterprise-search"
            defaultValue={t('All')}
          >
            <ComboboxOption value={t('All')} onClick={selectAllEnterprises}>
              {t('All')}
            </ComboboxOption>
            {enterprises?.map(enterprise => (
              <ComboboxOption
                key={enterprise.id}
                value={enterprise.name}
                onClick={() => selectEnterprise(enterprise.id)}
              >
                {enterprise.name}
              </ComboboxOption>
            ))}
          </Combobox>
        </FieldWrap>
      )}

      <SecurityEventLogFilters
        pending={pending}
        applied={applied}
        onChange={handleFilterChange}
        onApply={handleApply}
        onClear={handleClear}
        onExport={handleExport}
        onRefresh={refresh}
      />

      {error && !errorDismissed && (
        <ContentToast
          type="caution"
          show
          onDismiss={() => setErrorDismissed(true)}
        >
          <strong>{t('Error loading security events -')}</strong>
          <code>{error.message}</code>
        </ContentToast>
      )}

      {exportError && (
        <ContentToast
          type="caution"
          show
          onDismiss={() => setExportError(null)}
        >
          <strong>{t('Error exporting security events -')}</strong>
          <code>{exportError.message}</code>
        </ContentToast>
      )}

      {loading ? (
        <Loader label={t('Loading security events...')} tabIndex={-1} />
      ) : (
        <>
          <SecurityEventLogTable
            events={events}
            onViewEvent={setSelectedEvent}
          />

          <Pagination
            className={styles.pagination}
            totalItems={totalItems}
            itemsPerPage={ITEMS_PER_PAGE}
            currentPage={currentPage}
            statusLabel={
              <span>
                {t('Showing')} <strong>{rangeStart}</strong> &ndash;{' '}
                <strong>{rangeEnd}</strong> {t('of')}{' '}
                <strong>{totalItems}</strong>
              </span>
            }
            onNextPageClick={() =>
              setCurrentPage(p => Math.min(p + 1, totalPages))
            }
            onPreviousPageClick={() => setCurrentPage(p => Math.max(p - 1, 1))}
            onFirstPageClick={() => setCurrentPage(1)}
            onLastPageClick={() => setCurrentPage(totalPages)}
            firstPageLabel={t('First page')}
            lastPageLabel={t('Last page')}
            nextPageLabel={t('Next page')}
            previousPageLabel={t('Previous page')}
          />
        </>
      )}

      {selectedEvent && (
        <SecurityEventLogDetail
          event={selectedEvent}
          onClose={() => setSelectedEvent(null)}
        />
      )}
    </>
  );
};

export default SecurityEventLogTab;
